import { useCallback, useEffect, useMemo, useState } from 'react';
import { ViewHeader } from '../components/ViewHeader';
import { searchTasks } from '../utils/searchTasks';
import { SCOPES } from '../scopes';
import type { Task } from '../types';
import * as api from '../utils/apiClient';

function formatDeletedAt(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function TrashView() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setTasks(await api.listTasks('deleted'));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filtered = useMemo(() => searchTasks(tasks, query), [tasks, query]);
  const sorted = useMemo(
    () =>
      [...filtered].sort(
        (a, b) => (b.deletedAt ?? '').localeCompare(a.deletedAt ?? '')
      ),
    [filtered]
  );

  const onRestore = useCallback(async (task: Task) => {
    setBusyId(task.taskId);
    try {
      await api.restoreTask(task.scope, task.taskId);
      setTasks((prev) => prev.filter((t) => t.taskId !== task.taskId));
    } finally {
      setBusyId(null);
    }
  }, []);

  const onPurge = useCallback(async (task: Task) => {
    if (!window.confirm(`Permanently delete "${task.title}"? This cannot be undone.`)) return;
    setBusyId(task.taskId);
    try {
      await api.purgeTask(task.scope, task.taskId);
      setTasks((prev) => prev.filter((t) => t.taskId !== task.taskId));
    } finally {
      setBusyId(null);
    }
  }, []);

  return (
    <div className="scope-view">
      <ViewHeader title="Trash" searchValue={query} onSearchChange={setQuery} />
      <div className="pane-wrap">
        <section className="list-pane" aria-label="Deleted tasks">
          {loading ? (
            <div className="muted">Loading…</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : sorted.length === 0 ? (
            <div className="task-list-empty">Trash is empty.</div>
          ) : (
            <div className="task-list" role="list">
              {sorted.map((task) => {
                const scopeColor = SCOPES.find((s) => s.id === task.scope)?.color ?? 'transparent';
                const busy = busyId === task.taskId;
                return (
                  <div
                    key={task.taskId}
                    className="task-row is-deleted"
                    style={{ ['--row-accent' as string]: scopeColor }}
                    role="listitem"
                  >
                    <div />
                    <div className="task-row-body">
                      <div className="task-row-title-line">
                        <div className="task-row-title">
                          <span>{task.title}</span>
                        </div>
                        <span className="task-row-due">
                          deleted {formatDeletedAt(task.deletedAt)}
                        </span>
                      </div>
                      <div className="trash-actions">
                        <button
                          disabled={busy}
                          onClick={() => void onRestore(task)}
                        >
                          Restore
                        </button>
                        <button
                          className="danger"
                          disabled={busy}
                          onClick={() => void onPurge(task)}
                        >
                          Delete forever
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
